import client from "../../external/open-api";
import { components } from "../../external/schema/order-link-api-schema";
import { ApplicationError } from "../../models/errors/application-error";
import {
	ApplicationReturn,
	ApplicationReturnList,
	ReturnStatus,
} from "../../models/returns/applicant-return";
import BaseService from "../base-service";
import IReturnsService from "./returns-service-interface";

export default class ReturnsService extends BaseService implements IReturnsService {
	async get(
		currentPosition: number,
		fetchNext: number,
		fetchAll: boolean,
		searchText: string,
		userOnly: boolean,
		status?: ReturnStatus,
        supplierId?: number,
        branchId?: number
	): Promise<ApplicationReturnList> {
		const { data, error } = await this.withRetry(() =>
			client.GET("/v{version}/api/returns", {
				params: {
					path: { version: "1" },
					query: {
						"Paging.CurrentPosition": currentPosition,
						"Paging.FetchNext": fetchNext,
						"Paging.FetchAll": fetchAll,
						SearchText: searchText,
						UserOnly: userOnly,
						Status: status,
                        branchId: branchId,
                        supplierId: supplierId,
                    },
                },
            })
        );
					

		if (!data?.data || !data?.success || error) {
			throw new ApplicationError("Failed to fetch returns");
		}

		return {
			returns: this.mapToApplicationReturns(data.data.returns ?? []),
			count: data.data.count ?? 0,
			currentPosition: data.data.currentPosition ?? 0,
			loading: false,
		};
	}

	async getById(id: number): Promise<ApplicationReturn> {
		const { data, error } = await this.withRetry(() =>
			client.GET("/v{version}/api/returns/{id}", {
				params: {
					path: { version: "1", id },
				},
			})
		);

		if (!data?.data || !data?.success || error) {
			throw new ApplicationError("Failed to fetch return");
		}

		return this.mapToApplicationReturn(data.data);
	}

	async insert(applicationReturn: ApplicationReturn): Promise<ApplicationReturn> {
		const { data, error } = await this.withRetry(() =>
			client.POST("/v{version}/api/returns", {
				params: {
					path: { version: "1" },
				},
				body: this.mapToReturnDto(applicationReturn),
			})
		);

		if (!data?.data || !data?.success || error) {
			throw new ApplicationError("Failed to create return");
		}

		return this.mapToApplicationReturn(data.data);
	}

	async update(id: number, applicationReturn: ApplicationReturn): Promise<ApplicationReturn> {
		const { data, error } = await this.withRetry(() =>
			client.PUT("/v{version}/api/returns/{id}", {
				params: {
					path: { version: "1", id },
				},
				body: this.mapToReturnDto(applicationReturn),
			})
		);

		if (!data?.data || !data?.success || error) {
			throw new ApplicationError("Failed to update return");
		}

		return this.mapToApplicationReturn(data.data);
	}

	async delete(id: number): Promise<boolean> {
		const { data, error } = await this.withRetry(() =>
			client.DELETE("/v{version}/api/returns/{id}", {
				params: {
					path: { version: "1", id },
				},
			})
		);

		if (!data?.success || error) {
			throw new ApplicationError("Failed to delete return");
		}

		return Boolean(data.data);
	}

	private mapToApplicationReturn(data: components["schemas"]["ReturnDTO"]): ApplicationReturn {
		return {
			id: data.id ?? 0,
			supplierId: data.supplierId ?? 0,
			supplierName: data.supplierName ?? null,
			supplierProductId: data.supplierProductId ?? 0,
			branchId: data.branchId ?? 0,
			branchName: data.branchName ?? null,    
			productName: data.productName ?? null,
			returnQuantity: data.returnQuantity ?? 0,
			notes: data.notes ?? null,
			status: (data.status ?? "New") as ReturnStatus,
			created: data.created ? new Date(data.created) : new Date(),
			createdBy: data.createdBy ?? null,
			updated: data.updated ? new Date(data.updated) : null,
			updatedBy: data.updatedBy ?? null,
		};
	}

	private mapToApplicationReturns(data: components["schemas"]["ReturnDTO"][]): ApplicationReturn[] {
		return data.map((x) => this.mapToApplicationReturn(x));
	}

	private mapToReturnDto(applicationReturn: ApplicationReturn): components["schemas"]["ReturnDTO"] {
		return {
			id: applicationReturn.id,
			supplierId: applicationReturn.supplierId,
			supplierName: applicationReturn.supplierName,
			supplierProductId: applicationReturn.supplierProductId,
            branchId: applicationReturn.branchId,
            branchName: applicationReturn.branchName,
			productName: applicationReturn.productName,
			returnQuantity: applicationReturn.returnQuantity,
			notes: applicationReturn.notes,
			status: applicationReturn.status,
			created: applicationReturn.created.toISOString(),
			createdBy: applicationReturn.createdBy,
			updated: applicationReturn.updated ? applicationReturn.updated.toISOString() : null,
			updatedBy: applicationReturn.updatedBy,
		};
	}
}
